import { hexToRgba } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/chartUtils";

/**
 * Shared logic for the bucketed scatter charts (SOHScatterChart, RULScatterChart).
 *
 * The "Unavailable" bucket (category.isUnavailable: no SOH / no RUL) is a normal series,
 * drawn first (underneath), at y = 0, as solid grey dots. SOHScatterChart opts out
 * (plotUnavailable: false): its legend keeps the count, but no dots are drawn.
 *
 * Note: unavailableBucket.js has isUnavailableValue, which also rejects negatives.
 * isFiniteValue here only asks "can this be placed on an axis".
 */

/** True for a value that converts to a finite number. null, undefined and "" are not. */
export function isFiniteValue(value) {
  return value != null && value !== "" && Number.isFinite(Number(value));
}

/** y position of every Unavailable dot. */
export const UNAVAILABLE_Y = 0;

/**
 * Orders and filters the visible groups into the groups that become series.
 * Range buckets keep only the batteries `isPlottable` accepts; the unavailable group
 * keeps every battery (all of them are drawn at y = 0) and is moved FIRST, so it is drawn
 * underneath and its rings never block hover / click on real low-value points.
 * With plotUnavailable: false the unavailable group is left out of plottedGroups (no
 * series, so no dots and no 0 forced onto the axes); unavailableTotal still counts it.
 *
 * Series, colours, markers and the hover / click index lookups all use plottedGroups,
 * so series index i is plottedGroups[i] and data index j is plottedGroups[i].batteries[j].
 *
 * @returns {{
 *   plottedGroups:    Array<{category, batteries[]}>,
 *   unavailableTotal: number|null,  // every battery of the visible unavailable group,
 *                                   // null when that group is not visible
 * }}
 */
export function buildPlotGroups(groups, { isPlottable, plotUnavailable = true }) {
  const ranges = [];
  let unavailable = null;
  (groups ?? []).forEach((group) => {
    if (group?.category?.isUnavailable) {
      unavailable = group;
      return;
    }
    if (group?.category) ranges.push({ ...group, batteries: (group.batteries ?? []).filter(isPlottable) });
  });
  if (!unavailable) return { plottedGroups: ranges, unavailableTotal: null };
  const batteries = unavailable.batteries ?? [];
  if (!plotUnavailable) return { plottedGroups: ranges, unavailableTotal: batteries.length };
  return {
    plottedGroups:    [{ ...unavailable, batteries }, ...ranges],
    unavailableTotal: batteries.length,
  };
}

/**
 * ApexCharts series for the plotted groups.
 * An empty series list makes ApexCharts drop the axes; keep one empty series instead.
 * A fresh array is returned each time, since ApexCharts may mutate series.
 * @param {Function} toPoint  (battery, category) => { x, y }
 */
export function buildScatterSeries(plottedGroups, toPoint) {
  if (plottedGroups.length === 0) return [{ name: "", data: [] }];
  return plottedGroups.map(({ category, batteries }) => ({
    name: category.name,
    data: batteries.map((b) => toPoint(b, category)),
  }));
}

/** Series colours; one fallback colour for the single empty series. */
export function buildScatterColors(plottedGroups, fallbackColor) {
  return plottedGroups.length ? plottedGroups.map(({ category }) => category.color) : [fallbackColor];
}

/**
 * Per-series marker options. Every series, the unavailable one included, is a solid dot
 * in its own colour with a white ring.
 */
export function buildScatterMarkers(plottedGroups, { size = 4, strokeWidth = 2 } = {}) {
  const count = plottedGroups.length || 1;
  return {
    size,
    strokeWidth:  Array(count).fill(strokeWidth),
    strokeColors: Array(count).fill("#ffffff"),
    fillOpacity:  Array(count).fill(1),
    hover:        { sizeOffset: 2 },
  };
}

/**
 * sx for a legend colour swatch: a solid dot in the bucket colour, lighter when faded.
 * Same for range buckets and the unavailable bucket, matching the chart markers.
 */
export function getLegendSwatchSx(category, isFaded = false) {
  const color = category?.color ?? "#CCCCCC";
  return {
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
    bgcolor: isFaded ? hexToRgba(color, 0.35) : color,
  };
}

/** Finite x and y values of every point in the series, for axis ranges. */
export function collectAxisValues(series) {
  const xValues = [];
  const yValues = [];
  (series ?? []).forEach(({ data }) => {
    (data ?? []).forEach((p) => {
      if (isFiniteValue(p?.x)) xValues.push(Number(p.x));
      if (isFiniteValue(p?.y)) yValues.push(Number(p.y));
    });
  });
  return { xValues, yValues };
}

/**
 * Values for the y axis. Series i is plottedGroups[i] (see buildPlotGroups).
 *   - no real value (no drawn range-bucket point, e.g. only Unavailable dots or nothing
 *     at all) -> `fallback`, so the axis is a full default range, not 0..5
 *   - otherwise the real values, plus UNAVAILABLE_Y when any Unavailable dot is drawn
 */
export function resolveYAxisValues(series, plottedGroups, fallback) {
  const realValues = [];
  let hasUnavailableDots = false;
  (series ?? []).forEach(({ data }, i) => {
    const points = data ?? [];
    if (plottedGroups?.[i]?.category?.isUnavailable) {
      if (points.length > 0) hasUnavailableDots = true;
      return;
    }
    points.forEach((p) => {
      if (isFiniteValue(p?.y)) realValues.push(Number(p.y));
    });
  });
  if (realValues.length === 0) return [...fallback];
  return hasUnavailableDots ? [...realValues, UNAVAILABLE_Y] : realValues;
}

/** SOH y axis when no real SOH is drawn: 0..100 %. */
export const SOH_Y_FALLBACK = [0, 100];

/**
 * RUL y axis when no real RUL is drawn: empty, so computeNiceAxis returns its default
 * 0..100 range (the old [0] fallback gave -1..1).
 */
export const RUL_Y_FALLBACK = [];

// ── SOH ───────────────────────────────────────────────────────────────────────

/** Range-bucket batteries need a finite SOH. Null SOH never falls back to y = 0 there. */
export const isSOHPlottable = (battery) => isFiniteValue(battery?.soh);

/** Where a SOH point without a finite x value is drawn. */
export const SOH_MISSING_X_AT = 0;

/**
 * SOH point. x = the active x value, or 0 when it is missing; y = SOH, or 0 for the
 * unavailable bucket. So no odometer and no SOH gives (0, 0).
 */
export function toSOHPoint(battery, xKey, category) {
  const rawX = battery?.[xKey];
  return {
    x: isFiniteValue(rawX) ? Number(rawX) : SOH_MISSING_X_AT,
    y: category?.isUnavailable ? UNAVAILABLE_Y : Number(battery?.soh),
  };
}

// ── RUL ───────────────────────────────────────────────────────────────────────

/** Range-bucket batteries need a manufacturing date and a RUL value for the active unit. */
export const isRULPlottable = (battery, yKey) =>
  isFiniteValue(battery?.mfgDecimalYear) && isFiniteValue(battery?.[yKey]);

/**
 * Real manufacturing years of every plotted battery, for the RUL x axis. Batteries
 * without one (only possible in the unavailable series) are left out, so they never
 * widen the axis.
 */
export function collectRULAxisXValues(plottedGroups) {
  const values = [];
  (plottedGroups ?? []).forEach(({ batteries }) => {
    (batteries ?? []).forEach((b) => {
      if (isFiniteValue(b?.mfgDecimalYear)) values.push(Number(b.mfgDecimalYear));
    });
  });
  return values;
}

/**
 * RUL point. x = manufacturing year; a battery without one (unavailable series only) is
 * pinned to `xAxisMin`, the bottom-left corner, never year 0. y = RUL, or 0 for the
 * unavailable bucket.
 */
export function toRULPoint(battery, yKey, category, xAxisMin) {
  return {
    x: isFiniteValue(battery?.mfgDecimalYear) ? Number(battery.mfgDecimalYear) : xAxisMin,
    y: category?.isUnavailable ? UNAVAILABLE_Y : Number(battery?.[yKey]),
  };
}

// ── Axes and legend ───────────────────────────────────────────────────────────

/**
 * Smallest step for a km axis so ticks never format to the same label:
 * formatKmAxisTick rounds to whole km below 1000 and to 0.1k (100 km) above.
 */
export function kmAxisMinStep(values) {
  const maxAbs = (values ?? []).reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  return maxAbs >= 1000 ? 100 : 1;
}

/**
 * Legend text. The unavailable bucket shows its count, e.g. "SOH Unavailable (221)";
 * range buckets, a zero count or an unknown count show the display label alone.
 */
export function buildLegendLabel(category, unavailableCount) {
  const label = category?.displayLabel ?? category?.name ?? "";
  if (!category?.isUnavailable || !Number.isFinite(unavailableCount) || unavailableCount <= 0) return label;
  return `${label} (${unavailableCount.toLocaleString()})`;
}

/** Number of batteries in the unavailable bucket, by the batteries' bucket field. */
export function countUnavailableBatteries(batteries, buckets, categoryKey) {
  const name = (buckets ?? []).find((b) => b?.isUnavailable)?.name;
  if (name == null) return 0;
  return (batteries ?? []).filter((b) => b?.[categoryKey] === name).length;
}
